import { createServerFn } from '@tanstack/react-start'
import { getRequestIP } from '@tanstack/react-start/server'

import { isRateLimited } from '@/lib/rate-limit'

const SEARCH_ENDPOINT = 'https://recherche-entreprises.api.gouv.fr/search'

export interface EstablishmentSummary {
  siret: string
  name: string
  address: string
  apeCode: string
  isUai: boolean
  isFiness: boolean
}

export interface CompanySearchResult {
  siren: string
  siret: string
  name: string
  apeCode: string
  address: string
  establishmentsCount: number
  openEstablishmentsCount: number
  isUai: boolean
  isFiness: boolean
  /** Active establishments of this company, one per site — used to detect
      multiple sectors within the same company (e.g. head office + factory). */
  establishments: EstablishmentSummary[]
}

interface RawEstablishment {
  siret: string
  adresse?: string
  activite_principale?: string
  etat_administratif: string
  nom_commercial?: string | null
  liste_enseignes?: string[] | null
  libelle_commune?: string
  liste_uai?: string[] | null
  liste_finess?: string[] | null
}

interface RawSearchResult {
  siren: string
  nom_complet: string
  nombre_etablissements: number
  nombre_etablissements_ouverts: number
  etat_administratif: string
  activite_principale: string
  siege?: { adresse?: string; siret?: string }
  complements?: { est_uai?: boolean; est_finess?: boolean }
  matching_etablissements?: RawEstablishment[]
}

interface RawSearchResponse {
  results: RawSearchResult[]
}

function mapEstablishment(raw: RawEstablishment, companyName: string): EstablishmentSummary {
  return {
    siret: raw.siret,
    name: raw.nom_commercial ?? raw.liste_enseignes?.[0] ?? raw.libelle_commune ?? companyName,
    address: raw.adresse ?? '',
    apeCode: raw.activite_principale ?? '',
    isUai: Array.isArray(raw.liste_uai) && raw.liste_uai.length > 0,
    isFiness: Array.isArray(raw.liste_finess) && raw.liste_finess.length > 0,
  }
}

export function mapApiResult(raw: RawSearchResult): CompanySearchResult {
  return {
    siren: raw.siren,
    siret: raw.siege?.siret ?? '',
    name: raw.nom_complet,
    apeCode: raw.activite_principale ?? '',
    address: raw.siege?.adresse ?? '',
    establishmentsCount: raw.nombre_etablissements ?? 0,
    openEstablishmentsCount: raw.nombre_etablissements_ouverts ?? 0,
    isUai: raw.complements?.est_uai ?? false,
    isFiness: raw.complements?.est_finess ?? false,
    establishments: (raw.matching_etablissements ?? [])
      .filter((e) => e.etat_administratif === 'A')
      .map((e) => mapEstablishment(e, raw.nom_complet)),
  }
}

export interface SearchCompaniesInput {
  query: string
}

export interface SearchCompaniesSuccess {
  ok: true
  results: CompanySearchResult[]
}

export interface SearchCompaniesFailure {
  ok: false
  error: 'validation' | 'server'
}

export type SearchCompaniesResponse =
  | SearchCompaniesSuccess
  | SearchCompaniesFailure

export async function searchCompaniesImpl(
  input: SearchCompaniesInput,
): Promise<SearchCompaniesResponse> {
  const query = input.query?.trim() ?? ''
  if (query.length < 2) {
    return { ok: false, error: 'validation' }
  }

  try {
    const url = `${SEARCH_ENDPOINT}?q=${encodeURIComponent(query)}&limite=8`
    const response = await fetch(url, { signal: AbortSignal.timeout(4000) })
    if (!response.ok) {
      return { ok: false, error: 'server' }
    }

    const data = (await response.json()) as RawSearchResponse
    const results = (data.results ?? [])
      .filter((raw) => raw.etat_administratif === 'A')
      .map(mapApiResult)

    return { ok: true, results }
  } catch (error) {
    console.error('[searchCompanies] request failed', error)
    return { ok: false, error: 'server' }
  }
}

export const searchCompanies = createServerFn({ method: 'POST' })
  .validator((data: unknown) => data as SearchCompaniesInput)
  .handler(async ({ data }) => {
    const ip = getRequestIP({ xForwardedFor: true }) ?? 'unknown'
    if (isRateLimited(`company-search:${ip}`, { max: 20, windowMs: 60_000 })) {
      return { ok: false, error: 'server' } as const
    }
    return searchCompaniesImpl(data)
  })
